import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { Plus, PencilSimple, Trash, Wrench, WarningCircle, CheckCircle, ArrowRight, Receipt } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  intake: { label: 'Prise en charge', color: 'bg-amber-100 text-amber-700' },
  shipped: { label: 'Envoyé au fournisseur', color: 'bg-blue-100 text-blue-700' },
  repaired: { label: 'Réparé', color: 'bg-violet-100 text-violet-700' },
  returned_to_client: { label: 'Remis au client', color: 'bg-green-100 text-green-700' },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

const SAV = () => {
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, sav: null });
  const [newSav, setNewSav] = useState({ invoice_id: '', invoice_item_index: -1, intake_notes: '' });
  const [editForm, setEditForm] = useState({ repair_notes: '', returned_at: '', supplier: '', status: '' });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sav, inv] = await Promise.all([
        axios.get(`${API_URL}/api/sav`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoices`, { withCredentials: true }),
      ]);
      setItems(sav.data);
      setInvoices(inv.data);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const selectedInvoice = useMemo(() => invoices.find(i => i.id === newSav.invoice_id), [invoices, newSav.invoice_id]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (newSav.invoice_item_index < 0) {
      toast.error('Sélectionnez un article concerné');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/sav`, newSav, { withCredentials: true });
      toast.success('Dossier SAV créé');
      setCreateDialog(false);
      setNewSav({ invoice_id: '', invoice_item_index: -1, intake_notes: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const openEdit = (sav) => {
    setEditDialog({ open: true, sav });
    setEditForm({
      repair_notes: sav.repair_notes || '',
      returned_at: sav.returned_at ? sav.returned_at.slice(0, 10) : '',
      supplier: sav.supplier || '',
      status: sav.status || 'intake',
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        repair_notes: editForm.repair_notes,
        returned_at: editForm.returned_at ? new Date(editForm.returned_at).toISOString() : null,
        supplier: editForm.supplier,
        status: editForm.status,
      };
      await axios.put(`${API_URL}/api/sav/${editDialog.sav.id}`, payload, { withCredentials: true });
      toast.success('SAV mis à jour');
      setEditDialog({ open: false, sav: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (sav) => {
    if (!(await confirm({ title: 'Supprimer ce SAV ?', destructive: true }))) return;
    try {
      await axios.delete(`${API_URL}/api/sav/${sav.id}`, { withCredentials: true });
      toast.success('SAV supprimé');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const daysSince = (iso) => {
    if (!iso) return 0;
    return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="sav-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">SAV — Service Après Vente</h1>
            <p className="text-slate-600">Suivez les produits sous garantie pris en charge</p>
          </div>
          <Button onClick={() => setCreateDialog(true)} data-testid="create-sav-btn" className="bg-orange-500 hover:bg-orange-400 text-white">
            <Plus size={20} weight="bold" className="mr-2" /> Nouvelle prise en charge
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">
            <Wrench size={48} className="mx-auto text-slate-300 mb-3" />
            Aucun dossier SAV
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Facture</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Client</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Produit</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Fournisseur</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Prise en charge</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Remis au client</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Statut</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(sav => {
                  const days = daysSince(sav.intake_at);
                  const overdue = days > 15 && sav.status !== 'returned_to_client';
                  return (
                    <tr key={sav.id} data-testid={`sav-row-${sav.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${overdue ? 'bg-red-50 text-red-900' : ''}`}>
                      <td className="py-3 px-4 text-sm font-medium">{sav.invoice_number || '—'}</td>
                      <td className="py-3 px-4 text-sm">{sav.client_name}</td>
                      <td className="py-3 px-4 text-sm">{sav.product_name}</td>
                      <td className="py-3 px-4 text-sm">{sav.supplier || <span className="text-slate-400 italic">non précisé</span>}</td>
                      <td className="py-3 px-4 text-sm">
                        {fmtDate(sav.intake_at)}
                        {overdue && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-red-700">
                            <WarningCircle size={14} weight="fill" /> {days}j
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">{fmtDate(sav.returned_at)}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${STATUS_LABELS[sav.status]?.color || 'bg-slate-100'}`}>
                          {STATUS_LABELS[sav.status]?.label || sav.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => openEdit(sav)} data-testid={`edit-sav-${sav.id}`} className="inline-flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-md mr-1">
                          <PencilSimple size={18} />
                        </button>
                        {isAdmin && (
                          <button onClick={() => handleDelete(sav)} data-testid={`delete-sav-${sav.id}`} className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-md">
                            <Trash size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Create dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent data-testid="sav-create-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nouvelle prise en charge SAV</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Facture concernée *</label>
                <select
                  value={newSav.invoice_id}
                  onChange={(e) => setNewSav({ ...newSav, invoice_id: e.target.value, invoice_item_index: -1 })}
                  data-testid="sav-invoice-select"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  required
                >
                  <option value="">— Sélectionner une facture —</option>
                  {invoices.filter(inv => inv.status !== 'cancelled').map(inv => (
                    <option key={inv.id} value={inv.id}>
                      {inv.invoice_number} — {inv.client_name} ({inv.items.length} article(s))
                    </option>
                  ))}
                </select>
              </div>
              {selectedInvoice && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Article concerné *</label>
                  <div className="space-y-2">
                    {selectedInvoice.items.map((it, idx) => (
                      <label key={idx} className="flex items-center gap-2 p-2 border border-slate-200 rounded hover:bg-slate-50 cursor-pointer">
                        <input
                          type="radio"
                          name="sav_item"
                          checked={newSav.invoice_item_index === idx}
                          onChange={() => setNewSav({ ...newSav, invoice_item_index: idx })}
                          data-testid={`sav-item-radio-${idx}`}
                        />
                        <span className="text-sm">{it.description} <span className="text-slate-400">(qté: {it.quantity}, type: {it.product_type || '—'})</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes de prise en charge</label>
                <textarea
                  value={newSav.intake_notes}
                  onChange={(e) => setNewSav({ ...newSav, intake_notes: e.target.value })}
                  data-testid="sav-intake-notes-input"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Symptôme rapporté, accessoires laissés, etc."
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateDialog(false)}>Annuler</Button>
                <Button type="submit" data-testid="submit-sav-btn" className="bg-blue-600 hover:bg-blue-500 text-white">Créer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit dialog */}
        <Dialog open={editDialog.open} onOpenChange={(o) => !o && setEditDialog({ open: false, sav: null })}>
          <DialogContent data-testid="sav-edit-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Modifier le dossier SAV</DialogTitle></DialogHeader>
            {editDialog.sav && (
              <form onSubmit={handleEdit} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                  <div className="flex items-center gap-2"><Receipt size={14} /> <strong>Facture :</strong> {editDialog.sav.invoice_number}</div>
                  <div><strong>Client :</strong> {editDialog.sav.client_name}</div>
                  <div><strong>Produit :</strong> {editDialog.sav.product_name}</div>
                  <div><strong>Pris en charge :</strong> {fmtDate(editDialog.sav.intake_at)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fournisseur (à qui envoyer le produit)</label>
                  <Input value={editForm.supplier} onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })} data-testid="sav-supplier-input" placeholder="Pré-rempli depuis la fiche article" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Statut</label>
                  <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} data-testid="sav-status-select" className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white">
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Réparation effectuée</label>
                  <textarea
                    value={editForm.repair_notes}
                    onChange={(e) => setEditForm({ ...editForm, repair_notes: e.target.value })}
                    data-testid="sav-repair-notes-input"
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Détaillez la réparation, pièces remplacées, durée, coût SAV..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date de remise au client</label>
                  <Input type="date" value={editForm.returned_at} onChange={(e) => setEditForm({ ...editForm, returned_at: e.target.value })} data-testid="sav-returned-at-input" />
                  <p className="text-xs text-slate-500 mt-1">Remplir cette date passera automatiquement le statut à « Remis au client ».</p>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditDialog({ open: false, sav: null })}>Annuler</Button>
                  <Button type="submit" data-testid="submit-sav-edit-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
                    <CheckCircle size={16} className="mr-1" /> Enregistrer
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SAV;
